import {Component, EventEmitter, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faPlus, faXmark} from '@fortawesome/free-solid-svg-icons';
import {JsonSchema} from '../schema-form.types';

/**
 * array of free-form strings → Eingabefeld mit „+“ (wie in der Figma-Vorlage), die erfassten Werte
 * als entfernbare Badges darunter.
 *
 * Übernommen wird per Enter, per „+“ **und beim Verlassen des Felds**. Früher nur per Enter: wer
 * einen Wert tippte und weiterklickte oder speicherte, verlor ihn still – das Feld zeigte ihn ja
 * weiter an. So gingen z. B. die IFC-Klasse der Relationsprüfung und der Wert „true“ einer
 * Eigenschaftskarte verloren, ohne dass es jemand merkte.
 */
@Component({
  selector: 'sf-chips',
  standalone: true,
  imports: [FaIconComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex flex-col gap-2 w-full">
      <div class="relative w-full">
        <input
          type="text"
          [value]="draft"
          [placeholder]="placeholder"
          (input)="draft = $any($event.target).value"
          (keydown.enter)="add($event)"
          (blur)="commit()"
          class="w-full h-[38px] pl-3 pr-9 text-sm text-gray-900 bg-gray-50 border border-gray-300 rounded-lg
                 focus:outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-200 transition-colors"/>
        <button
          type="button"
          [disabled]="!draft.trim()"
          (click)="commit()"
          title="Hinzufügen"
          aria-label="Hinzufügen"
          class="absolute right-0 top-0 h-[38px] w-9 flex items-center justify-center text-gray-400
                 hover:text-gray-700 disabled:hover:text-gray-400 disabled:cursor-default transition-colors">
          <fa-icon [icon]="faPlus"></fa-icon>
        </button>
      </div>
      @if (chips.length) {
        <div class="flex flex-wrap gap-2">
          @for (chip of chips; track $index) {
            <span class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-gray-200 text-gray-700 text-xs font-medium">
              {{ chip }}
              <button type="button" (click)="remove($index)" class="text-gray-500 hover:text-red transition-colors">
                <fa-icon [icon]="faXmark"></fa-icon>
              </button>
            </span>
          }
        </div>
      }
    </div>
  `,
})
export class SfChipsComponent {
  @Input() schema: JsonSchema | null = null;
  @Input() value: any;
  @Output() valueChange = new EventEmitter<any>();

  draft = '';
  protected readonly faPlus = faPlus;
  protected readonly faXmark = faXmark;

  get chips(): string[] {
    return Array.isArray(this.value) ? this.value : [];
  }

  get placeholder(): string {
    return this.schema?.['x-placeholder'] ?? 'Hinzufügen…';
  }

  add(event: Event): void {
    event.preventDefault();
    this.commit();
  }

  /** Übernimmt den Entwurf als Badge – leere Eingaben bleiben folgenlos (Blur nach Enter, leeres „+“). */
  commit(): void {
    const val = this.draft.trim();
    this.draft = '';
    if (!val) return;
    this.valueChange.emit([...this.chips, val]);
  }

  remove(index: number): void {
    this.valueChange.emit(this.chips.filter((_, i) => i !== index));
  }
}
